import React, { useCallback, useEffect, useState } from 'react';

type AddTabType =
  | 'new-dashboard'
  | 'add-dashboard'
  | 'copy-dashboard'
  | 'add-report'
  | 'add-performance-board'
  | 'add-driver-bonus-program-report'
  | 'add-driver-bonus-combined'
  | 'add-driver-details'
  | 'add-customer-lane-commitments-board'
  | 'copy-scorecard';

const useAddTabTypeOptions = ({
  setTitle,
  setSelectedDashboard,
  setSelectedPerformanceBoard,
  setSelectedCustomerLaneCommitmentsBoard,
  setSelectedReport,
  setSelectedBonusProgram,
  setSelectedScorecard,
  setLabelIds,
}: {
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setSelectedDashboard: React.Dispatch<
    React.SetStateAction<DashboardType | undefined>
  >;
  setSelectedPerformanceBoard: React.Dispatch<
    React.SetStateAction<PerformanceBoardTypes.Board | undefined>
  >;
  setSelectedCustomerLaneCommitmentsBoard: React.Dispatch<
    React.SetStateAction<CustomerLaneBoard | undefined>
  >;
  setSelectedReport: React.Dispatch<
    React.SetStateAction<ReportType | undefined>
  >;
  setSelectedBonusProgram: React.Dispatch<
    React.SetStateAction<BonusTypes.BonusProgram | undefined>
  >;
  setSelectedScorecard: React.Dispatch<
    React.SetStateAction<Scorecards.Scorecard | undefined>
  >;
  setLabelIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const [addTabType, setAddTabType] = useState<AddTabType>('new-dashboard');
  const clearSettings = useCallback(() => {
    setSelectedDashboard(undefined);
    setSelectedPerformanceBoard(undefined);
    setSelectedReport(undefined);
    setSelectedBonusProgram(undefined);
    setSelectedCustomerLaneCommitmentsBoard(undefined);
    setSelectedScorecard(undefined);
    setTitle('');
    setLabelIds([]);
  }, [
    setSelectedDashboard,
    setSelectedPerformanceBoard,
    setSelectedReport,
    setSelectedBonusProgram,
    setSelectedCustomerLaneCommitmentsBoard,
    setSelectedScorecard,
    setTitle,
    setLabelIds,
  ]);

  const buildOptions = useCallback((): RadioOption[] => {
    return [
      {
        key: 'new-dashboard',
        label: 'New Dashboard',
        isSelected: addTabType === 'new-dashboard',
        onSelected: () => {
          setAddTabType('new-dashboard');
          clearSettings();
        },
      },
      {
        key: 'add-dashboard',
        label: 'Add Dashboard',
        isSelected: addTabType === 'add-dashboard',
        onSelected: () => {
          setAddTabType('add-dashboard');
          clearSettings();
        },
      },
      {
        key: 'copy-dashboard',
        label: 'Create copy of Existing Dashboard',
        isSelected: addTabType === 'copy-dashboard',
        onSelected: () => {
          setAddTabType('copy-dashboard');
          clearSettings();
        },
      },
      {
        key: 'add-performance-board',
        label: 'Add Performance Board',
        isSelected: addTabType === 'add-performance-board',
        onSelected: () => {
          setAddTabType('add-performance-board');
          clearSettings();
        },
      },
      {
        key: 'add-customer-lane-commitments-board',
        label: 'Add Customer Lane Commitments Board',
        isSelected: addTabType === 'add-customer-lane-commitments-board',
        onSelected: () => {
          setAddTabType('add-customer-lane-commitments-board');
          clearSettings();
        },
      },
      {
        key: 'add-report',
        label: 'Add Report',
        isSelected: addTabType === 'add-report',
        onSelected: () => {
          setAddTabType('add-report');
          clearSettings();
        },
      },
      {
        key: 'add-driver-bonus-program-report',
        label: 'Add Driver Bonus Program Report',
        isSelected: addTabType === 'add-driver-bonus-program-report',
        onSelected: () => {
          setAddTabType('add-driver-bonus-program-report');
          clearSettings();
        },
      },
      {
        key: 'add-driver-bonus-combined',
        label: 'Add Combined Bonus Tab',
        isSelected: addTabType === 'add-driver-bonus-combined',
        onSelected: () => {
          setAddTabType('add-driver-bonus-combined');
          clearSettings();
        },
      },
      {
        key: 'add-driver-details',
        label: 'Add Driver Details',
        isSelected: addTabType === 'add-driver-details',
        onSelected: () => {
          setAddTabType('add-driver-details');
          clearSettings();
        },
      },
      {
        key: 'copy-scorecard',
        label: 'Create copy of existing KPI List',
        isSelected: addTabType === 'copy-scorecard',
        onSelected: () => {
          setAddTabType('copy-scorecard');
          clearSettings();
        },
      },
    ];
  }, [addTabType, clearSettings]);

  const [options, setOptions] = useState<RadioOption[]>(buildOptions);
  useEffect(() => {
    setOptions(buildOptions());
  }, [buildOptions]);

  return {
    options,
    addTabType,
  };
};

export default useAddTabTypeOptions;
